import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  Award,
  Clock,
  Copy,
  Download,
  GraduationCap,
  MoreVertical,
  Pencil,
  Plus,
  ScrollText,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { BulkSelectionToggle } from '@/components/data-table/BulkSelectionToggle';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import {
  exportToCSV,
  useBulkFormations,
  useDeleteFormation,
  useFormateurs,
  useFormations,
  useUpsertFormation,
} from '@/hooks/queries/useFormation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDA } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/useRole';
import type {
  FormationModalite,
  FormationNiveau,
  FormationRow,
  ParentSegmentType,
} from '@/lib/database.types';
import { FormationFormDialog } from './FormationFormDialog';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_FORMATIONS } from '@/lib/import-templates';
import { supabase } from '@/lib/supabase';
import {
  ModernCard,
  ModernCardBody,
  ModernCardFooter,
  ModernCardHeader,
} from '@/components/ui/modern-card';
import { EmptyStateIllustration } from '@/components/ui/empty-state-illustration';

const NIVEAU_LABEL: Record<FormationNiveau, string> = {
  debutant: 'Débutant',
  intermediaire: 'Intermédiaire',
  avance: 'Avancé',
  expert: 'Expert',
};

export function CataloguePage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));

  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);

  const { data: sousSegments = [] } = useAllSousSegments();
  const { data: formateurs = [] } = useFormateurs();
  const [niveau, setNiveau] = useState<FormationNiveau | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);

  // Filtre fin sous-segments (multi-select), persisté ?subseg=B2B-FIN,B2B-MGMT
  const [searchParams, setSearchParams] = useSearchParams();
  const subsegParam = searchParams.get('subseg') ?? '';
  const [subsegSelection, setSubsegSelection] = useState<string[]>(
    subsegParam ? subsegParam.split(',').filter(Boolean) : [],
  );

  // Sous-segments disponibles selon le grand segment
  const subsegOptions = useMemo(() => {
    const segments: ParentSegmentType[] =
      segment === 'all' ? ['B2B', 'B2C', 'CBTA'] : [segment as ParentSegmentType];
    return sousSegments.filter((s) => s.parent_segment && segments.includes(s.parent_segment));
  }, [sousSegments, segment]);

  // ID list final : intersection segmentIds + subsegSelection
  const finalSubsegIds = useMemo(() => {
    if (subsegSelection.length > 0) {
      return subsegSelection.filter((id) => segmentIds.includes(id));
    }
    return segmentIds;
  }, [subsegSelection, segmentIds]);

  const { data: formations = [], isLoading } = useFormations({
    sousSegmentIds: finalSubsegIds,
    statut: 'actif',
    search,
  });

  const filtered = useMemo(
    () => (niveau ? formations.filter((f) => f.niveau === niveau) : formations),
    [formations, niveau],
  );

  const sousSegmentMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sousSegments) m.set(s.id, s.nom);
    return m;
  }, [sousSegments]);
  const formateurMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of formateurs) m.set(f.id, `${f.prenom} ${f.nom}`);
    return m;
  }, [formateurs]);

  const upsert = useUpsertFormation();
  const remove = useDeleteFormation();
  const bulk = useBulkFormations();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FormationRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<FormationRow | null>(null);

  // Bulk selection
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);

  const toggleSelect = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = (): void => setSelected(new Set());
  const selectAll = (): void => setSelected(new Set(filtered.map((f) => f.id)));

  const defaultParentSegment: ParentSegmentType | undefined =
    segment === 'all' ? undefined : (segment as ParentSegmentType);

  useEffect(() => undefined, [eurlKostGroup]);

  // Sync subseg URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (subsegSelection.length === 0) next.delete('subseg');
    else next.set('subseg', subsegSelection.join(','));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsegSelection]);

  // Reset subsegSelection si segment change
  useEffect(() => {
    setSubsegSelection((cur) => cur.filter((id) => subsegOptions.some((s) => s.id === id)));
  }, [segment, subsegOptions]);

  const handleArchive = async (f: FormationRow): Promise<void> => {
    await upsert.mutateAsync({
      id: f.id,
      entreprise_id: f.entreprise_id,
      titre: f.titre,
      code: f.code,
      statut: f.statut === 'archive' ? 'actif' : 'archive',
    });
    toast.success(f.statut === 'archive' ? 'Formation réactivée' : 'Formation archivée');
  };

  const handleDuplicate = async (f: FormationRow): Promise<void> => {
    const newCode = `${f.code}-COPY`;
    await upsert.mutateAsync({
      entreprise_id: f.entreprise_id,
      sous_segment_id: f.sous_segment_id,
      code: newCode,
      titre: `${f.titre} (copie)`,
      description: f.description,
      objectifs: f.objectifs,
      prerequis: f.prerequis,
      niveau: f.niveau,
      duree_heures: f.duree_heures,
      duree_jours: f.duree_jours,
      modalites: f.modalites,
      prix_inter_unitaire: f.prix_inter_unitaire,
      prix_intra_forfait: f.prix_intra_forfait,
      prix_b2c: f.prix_b2c,
      certif_delivree: f.certif_delivree,
      code_certif: f.code_certif,
      validite_mois: f.validite_mois,
      formateur_principal_id: f.formateur_principal_id,
      est_partage_b2b: f.est_partage_b2b,
      statut: 'draft',
    });
    toast.success(`Formation dupliquée : ${newCode}`);
  };

  const selectedRows = filtered.filter((f) => selected.has(f.id));

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Catalogue formations"
        subtitle={`${filtered.length} formation${filtered.length > 1 ? 's' : ''} active${filtered.length > 1 ? 's' : ''}.`}
        actions={
          <div className="flex items-center gap-2">
            <SelecteurSousSegmentFormation value={segment} onChange={setSegment} />
            <BulkSelectionToggle
              enabled={selectionEnabled}
              onToggle={() => {
                if (selectionEnabled) clearSelection();
                setSelectionEnabled(!selectionEnabled);
              }}
              count={selected.size}
            />
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Importer XLSX/CSV
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouvelle formation
            </Button>
          </div>
        }
      />

      <BulkActionBar count={selected.size} onClear={clearSelection}>
        <BulkButton onClick={selectAll}>Tout sélectionner ({filtered.length})</BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'archive' });
            toast.success(`${r.success} formation(s) archivée(s)`);
            clearSelection();
          }}
        >
          <Archive className="h-3.5 w-3.5" /> Archiver
        </BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'unarchive' });
            toast.success(`${r.success} formation(s) réactivée(s)`);
            clearSelection();
          }}
        >
          <ArchiveRestore className="h-3.5 w-3.5" /> Réactiver
        </BulkButton>
        <BulkButton
          onClick={async () => {
            // Dupliquer N (séquentiel pour conserver suffixes COPY)
            let ok = 0;
            for (const f of selectedRows) {
              try {
                await handleDuplicate(f);
                ok++;
              } catch {
                /* ignore */
              }
            }
            toast.success(`${ok} formation(s) dupliquée(s)`);
            clearSelection();
          }}
        >
          <Copy className="h-3.5 w-3.5" /> Dupliquer
        </BulkButton>
        <BulkButton
          onClick={() => {
            exportToCSV(
              selectedRows.map((f) => ({
                code: f.code,
                titre: f.titre,
                niveau: f.niveau,
                duree_jours: f.duree_jours ?? '',
                prix_inter: f.prix_inter_unitaire ?? '',
                prix_b2c: f.prix_b2c ?? '',
                statut: f.statut,
                certif: f.certif_delivree ? 'oui' : 'non',
              })),
              `formations-${selectedRows.length}.csv`,
            );
            toast.success(`${selectedRows.length} ligne(s) exportée(s)`);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </BulkButton>
        {isAdmin && (
          <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </BulkButton>
        )}
      </BulkActionBar>

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Niveau</label>
          <select
            value={niveau}
            onChange={(e) => setNiveau(e.target.value as FormationNiveau | '')}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Tous —</option>
            <option value="debutant">Débutant</option>
            <option value="intermediaire">Intermédiaire</option>
            <option value="avance">Avancé</option>
            <option value="expert">Expert</option>
          </select>
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <label className="text-xs text-muted-foreground">
            Sous-segments ({subsegSelection.length || subsegOptions.length})
          </label>
          <div className="flex flex-wrap gap-1">
            {subsegOptions.map((s) => {
              const active = subsegSelection.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() =>
                    setSubsegSelection((cur) =>
                      cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id],
                    )
                  }
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px]',
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-secondary',
                  )}
                >
                  {s.nom}
                </button>
              );
            })}
            {subsegSelection.length > 0 && (
              <button
                type="button"
                onClick={() => setSubsegSelection([])}
                className="rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary"
              >
                <X className="h-2.5 w-2.5" /> Effacer
              </button>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Recherche</label>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Titre, code…"
            className="h-9 w-56"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-sm text-muted-foreground">Chargement…</div>
      ) : filtered.length === 0 ? (
        <EmptyStateIllustration
          type="formations"
          title="Aucune formation au catalogue"
          description="Commencez par créer une formation, importer un XLSX, ou ajustez vos filtres."
          action={
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Nouvelle formation
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f) => {
            const isSelected = selected.has(f.id);
            return (
              <ModernCard key={f.id} variant="formation" selected={isSelected}>
                {selectionEnabled && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(f.id)}
                    className="absolute left-3 top-3 z-10 h-4 w-4"
                  />
                )}
                <div className="absolute right-2 top-2 z-10">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          setEditing(f);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void handleDuplicate(f)}>
                        <Copy className="h-3.5 w-3.5" /> Dupliquer
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void handleArchive(f)}>
                        <Archive className="h-3.5 w-3.5" />
                        {f.statut === 'archive' ? 'Réactiver' : 'Archiver'}
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem
                          onSelect={() => setConfirmDelete(f)}
                          className="text-destructive"
                        >
                          Supprimer
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <Link to={`/formation/catalogue/${f.id}`} className="block">
                  <ModernCardHeader
                    icon={<GraduationCap className="h-4 w-4" />}
                    title={f.titre}
                    subtitle={
                      <span className="flex items-center gap-1.5">
                        <span className="font-mono">{f.code}</span>
                        {f.sous_segment_id && (
                          <Badge variant="outline" className="text-[9px]">
                            {sousSegmentMap.get(f.sous_segment_id) ?? '—'}
                          </Badge>
                        )}
                      </span>
                    }
                    trailing={
                      f.certif_delivree ? (
                        <Badge variant="success" className="text-[10px]">
                          <ScrollText className="h-2.5 w-2.5" /> Certif
                        </Badge>
                      ) : undefined
                    }
                  />
                  <ModernCardBody>
                    {f.description && (
                      <p className="line-clamp-2 text-muted-foreground">{f.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <Badge variant="secondary">{NIVEAU_LABEL[f.niveau]}</Badge>
                      {f.duree_jours && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" /> {f.duree_jours}j
                        </span>
                      )}
                      {f.formateur_principal_id && formateurMap.has(f.formateur_principal_id) && (
                        <span className="inline-flex items-center gap-1 truncate">
                          <GraduationCap className="h-2.5 w-2.5" />
                          {formateurMap.get(f.formateur_principal_id)}
                        </span>
                      )}
                    </div>
                  </ModernCardBody>
                  <ModernCardFooter>
                    <span className="num font-semibold">
                      {f.prix_b2c
                        ? formatDA(Number(f.prix_b2c))
                        : f.prix_inter_unitaire
                          ? formatDA(Number(f.prix_inter_unitaire))
                          : '—'}
                    </span>
                    {f.taux_satisfaction_moyen != null && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          Number(f.taux_satisfaction_moyen) >= 8 && 'text-success',
                        )}
                      >
                        <Award className="h-3 w-3" />
                        NPS {Number(f.taux_satisfaction_moyen).toFixed(1)}
                      </span>
                    )}
                  </ModernCardFooter>
                </Link>
              </ModernCard>
            );
          })}
        </div>
      )}

      {eurlKostGroup && (
        <FormationFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          entrepriseId={eurlKostGroup.id}
          defaultParentSegment={defaultParentSegment}
          editing={editing}
          onSaved={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      {eurlKostGroup && (
        <ImportDialog<Record<string, unknown>>
          open={importOpen}
          onOpenChange={setImportOpen}
          title="Importer le catalogue formations (XLSX/CSV)"
          generateTemplate={() => buildTemplate(TEMPLATE_FORMATIONS)}
          templateFilename={TEMPLATE_FORMATIONS.filename}
          fields={
            [
              { key: 'code', label: 'Code', required: true },
              { key: 'titre', label: 'Titre', required: true },
              { key: 'sous_segment', label: 'Sous-segment', required: true },
              { key: 'description', label: 'Description courte' },
              { key: 'objectifs', label: 'Objectifs' },
              { key: 'prerequis', label: 'Prérequis' },
              { key: 'duree_heures', label: 'Durée (heures)', required: true },
              { key: 'duree_jours', label: 'Durée (jours)', required: true },
              { key: 'niveau', label: 'Niveau', required: true },
              { key: 'modalite', label: 'Modalité', required: true },
              { key: 'prix_inter', label: 'Prix B2B inter (DZD)' },
              { key: 'prix_intra', label: 'Prix B2B intra (DZD)' },
              { key: 'prix_b2c', label: 'Prix B2C (DZD)' },
              { key: 'certif_delivree', label: 'Certif délivrée' },
              { key: 'code_certif', label: 'Code certif' },
              { key: 'validite_mois', label: 'Validité (mois)' },
              { key: 'formateur_email', label: 'Formateur principal (email)' },
              { key: 'statut', label: 'Statut', required: true },
            ] satisfies ImportField[]
          }
          onImport={async (rows) => {
            const errors: string[] = [];
            let success = 0;
            const ssMap = new Map<string, string>();
            for (const s of sousSegments) {
              ssMap.set(s.code.toLowerCase(), s.id);
              ssMap.set(s.nom.toLowerCase(), s.id);
            }
            const formateurMap = new Map<string, string>();
            for (const f of formateurs) {
              if (f.email) formateurMap.set(f.email.toLowerCase(), f.id);
            }
            for (let i = 0; i < rows.length; i++) {
              const r = rows[i];
              if (!r) continue;
              try {
                const ssId = ssMap.get(
                  String(r.sous_segment ?? '')
                    .trim()
                    .toLowerCase(),
                );
                if (!ssId) {
                  errors.push(`L${i + 2}: sous-segment inconnu "${r.sous_segment}"`);
                  continue;
                }
                const niveauRaw = String(r.niveau ?? 'debutant')
                  .trim()
                  .toLowerCase();
                const niveau: FormationNiveau = (
                  ['debutant', 'intermediaire', 'avance', 'expert'].includes(niveauRaw)
                    ? niveauRaw
                    : 'debutant'
                ) as FormationNiveau;
                const modaliteRaw = String(r.modalite ?? 'presentiel')
                  .trim()
                  .toLowerCase();
                const modalite: FormationModalite = (
                  ['presentiel', 'distanciel', 'blended'].includes(modaliteRaw)
                    ? modaliteRaw
                    : 'presentiel'
                ) as FormationModalite;
                const statutRaw = String(r.statut ?? 'actif')
                  .trim()
                  .toLowerCase();
                const statut = (
                  ['actif', 'draft', 'archive'].includes(statutRaw) ? statutRaw : 'draft'
                ) as 'actif' | 'draft' | 'archive';
                const formateur_principal_id = r.formateur_email
                  ? (formateurMap.get(String(r.formateur_email).trim().toLowerCase()) ?? null)
                  : null;
                const payload: Partial<FormationRow> = {
                  entreprise_id: eurlKostGroup.id,
                  sous_segment_id: ssId,
                  code: String(r.code).trim(),
                  titre: String(r.titre).trim(),
                  description: String(r.description ?? '').trim() || null,
                  objectifs: String(r.objectifs ?? '').trim() || null,
                  prerequis: String(r.prerequis ?? '').trim() || null,
                  duree_heures: Number(r.duree_heures || 0),
                  duree_jours: Number(r.duree_jours || 0),
                  niveau,
                  modalites: [modalite],
                  prix_inter_unitaire: r.prix_inter ? Number(r.prix_inter) : null,
                  prix_intra_forfait: r.prix_intra ? Number(r.prix_intra) : null,
                  prix_b2c: r.prix_b2c ? Number(r.prix_b2c) : null,
                  certif_delivree: String(r.certif_delivree ?? '')
                    .trim()
                    .toLowerCase()
                    .startsWith('o'),
                  code_certif: String(r.code_certif ?? '').trim() || null,
                  validite_mois: r.validite_mois ? Number(r.validite_mois) : null,
                  formateur_principal_id,
                  statut,
                };
                const { error } = await supabase.from('formations').insert(payload as never);
                if (error) errors.push(`L${i + 2}: ${error.message}`);
                else success++;
              } catch (e) {
                errors.push(`L${i + 2}: ${(e as Error).message}`);
              }
            }
            return { success, errors };
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer la formation « ${confirmDelete?.titre} » ?`}
        description="Action irréversible. Cette opération échouera s'il existe des sessions associées."
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await remove.mutateAsync(confirmDelete.id);
            toast.success('Formation supprimée');
          } catch (e) {
            toast.error((e as Error).message);
          }
          setConfirmDelete(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selected.size} formation${selected.size > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les sessions liées bloqueront la suppression (échec gracieux par formation)."
        consequences={[
          `${selected.size} ligne(s) effacées`,
          'Les évaluations / inscriptions liées sont conservées (pas de cascade)',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={bulk.isPending}
        onConfirm={async () => {
          const r = await bulk.mutateAsync({ ids: [...selected], action: 'delete' });
          if (r.errors.length === 0) toast.success(`${r.success} formation(s) supprimée(s)`);
          else
            toast.warning(`${r.success} OK · ${r.errors.length} échec(s) — ${r.errors[0] ?? ''}`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />
    </div>
  );
}
